import { defineStore } from 'pinia'
import type { User } from '@supabase/supabase-js'
import { normalizeBoolean } from '~/utils/normalizeBoolean'

export interface UserProfile {
  id: string
  email: string
  name?: string
  avatar_url?: string
  company_id?: string
  onboarding_completed?: boolean
  plan_selected?: boolean
  selected_plan?: string
  subscription_id?: string
  subscription_status?: string
  joined_via_invitation?: boolean
  is_master_admin?: boolean
  created_at: string
  updated_at: string
}

export interface UserState {
  user: User | null
  profile: UserProfile | null
  isLoading: boolean
  error: string | null
  lastProfileUpdate: Date | null
}

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    user: null, profile: null, isLoading: false, error: null, lastProfileUpdate: null
  }),

  getters: {
    isAuthenticated: (state): boolean => !!state.user,
    userMetadata: (state): any => state.user?.user_metadata || {},

    hasCompletedOnboarding(state): boolean {
      try {
        if (state.profile?.onboarding_completed !== undefined) {
          return normalizeBoolean(state.profile.onboarding_completed)
        }
        return normalizeBoolean(state.user?.user_metadata?.onboarding_completed)
      } catch { return false }
    },

    hasSelectedPlan(state): boolean {
      if (state.profile?.plan_selected !== undefined) return normalizeBoolean(state.profile.plan_selected)
      return normalizeBoolean(state.user?.user_metadata?.plan_selected)
    },

    selectedPlan(state): string | null {
      if (state.profile?.selected_plan) return state.profile.selected_plan
      return state.user?.user_metadata?.selected_plan || null
    },

    joinedViaInvitation(state): boolean {
      if (state.profile?.joined_via_invitation !== undefined) return normalizeBoolean(state.profile.joined_via_invitation)
      return normalizeBoolean(state.user?.user_metadata?.joined_via_invitation)
    },

    isMasterAdmin(state): boolean {
      if (state.profile?.is_master_admin !== undefined) return normalizeBoolean(state.profile.is_master_admin)
      return normalizeBoolean(state.user?.user_metadata?.is_master_admin)
    },

    isProfileStale: (state): boolean => {
      if (!state.lastProfileUpdate) return true
      return state.lastProfileUpdate < new Date(Date.now() - 10 * 60 * 1000)
    }
  },

  actions: {
    setUser(user: User | null) {
      this.user = user
      if (!user) { this.profile = null; this.lastProfileUpdate = null }
    },

    async fetchProfile(force: boolean = false): Promise<UserProfile | null> {
      if (!this.user) return null
      if (!force && !this.isProfileStale && this.profile) return this.profile

      this.isLoading = true
      this.error = null
      try {
        const response = await $fetch<{success: boolean, profile: UserProfile}>('/api/user/profile')
        if (response.success && response.profile) {
          this.profile = response.profile
          this.lastProfileUpdate = new Date()
        }
        return this.profile
      } catch (error: any) {
        console.error('Failed to fetch profile:', error)
        this.error = error.message || 'Failed to fetch profile'
        return null
      } finally {
        this.isLoading = false
      }
    },

    clearUserData() {
      this.user = null; this.profile = null; this.lastProfileUpdate = null; this.error = null
    },

    async initializeFromAuth() {
      const user = useSupabaseUser()
      if (user.value) { this.setUser(user.value); this.fetchProfile() }
      else { this.clearUserData() }
    }
  }
})
